import { useState, useCallback, useMemo, useEffect } from 'react';
import { DEFAULT_PAGE_SIZE, PAGE_SIZE_OPTIONS } from '@constants/pagination';
import {
  Table,
  Empty,
  Spin,
  Alert,
  Input,
  InputNumber,
  Typography,
  Tooltip,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useQuery, useMutation } from '@apollo/client';
import { useNavigate, useSearchParams } from 'react-router-dom';
import PageLayout from '@components/layout/PageLayout';
import { NSPButton } from '@components/ui/NSPButton';
import {
  GET_OATT_REVISION_DATA,
  SAVE_OATT_REVISION,
  GET_OATT_REVISION_NUMBERS,
  GET_OATT_ANCILLARY_RATES,
} from '@graphql/oattrevision.gql';
import { COMPUTE_OATT_REVISION } from '@graphql/oattcomputation.gql';
import styles from './OattPages.module.css';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import useSession from '@hooks/useSession';
import { useOattCustomer } from '@hooks/useOattcustomer';

const { Text } = Typography;

// ── Types ─────────────────────────────────────────────────────────────────────

interface OattRevisionRecord {
  oatt_data_id: number;
  seller: string;
  aref: string;
  customer_code: string;
  billing_period: string;
  start_time: string;
  stop_time: string;
  mw_granted: number | null;
  billing_rate: number | null;
  billing_type: string;
  ts_class: string;
  increment: string;
  ts_type: string;
  ts_window: string;
  hours: number | null;
  net_trans: number | null;
  asr_rv_net: number | null;
  asr_sc_net: number | null;
  total_charges: number | null;
  is_revision: boolean;
  revision_no: number;
  changes_from_original: string | null;
  /** Populated by the server: true when all validation records for this revision are approved. */
  revision_approved: boolean | null;
}

interface AncillaryRate {
  ancillary_service_type: string;
  frequency: string;
  rate: number;
}

type EditMap = Record<number, Partial<OattRevisionRecord>>;

interface PreviewFields {
  hours: number | null;
  net_trans: number | null;
  asr_sc_net: number | null;
  asr_rv_net: number | null;
  total_charges: number | null;
}

// ── Live-preview helpers (unchanged from original) ────────────────────────────

const ANC1_SERVICE = 'scheduling, system control, and dispatch services';
const ANC2_SERVICE = 'reactive supply and voltage control from generation sources service';

function parseDateLocal(raw: string | null | undefined): Date | null {
  if (!raw) return null;
  const cleaned = String(raw)
    .replace('T', ' ').replace(/\.\d+/, '').replace(/Z$/, '')
    .replace(/[+-]\d{2}:\d{2}$/, '').trim();
  const [datePart, timePart = '00:00:00'] = cleaned.split(' ');
  const [yyyy, mm, dd] = datePart.split('-').map(Number);
  const [hh = 0, mi = 0, ss = 0] = timePart.split(':').map(Number);
  if (!yyyy || !mm || !dd) return null;
  const d = new Date(yyyy, mm - 1, dd, hh, mi, ss);
  return isNaN(d.getTime()) ? null : d;
}

function computeDurationValue(increment: string, aref: string, start: Date | null, stop: Date | null): number | null {
  if (!aref?.trim() || !start || !stop) return null;
  const inc = increment.toUpperCase();
  const diffMs = stop.getTime() - start.getTime();
  const wholeDays = Math.round(diffMs / (1000 * 60 * 60 * 24));
  if (inc === 'MONTHLY') return 1;
  if (inc === 'YEARLY') return 1 / 12;
  if (inc === 'WEEKLY') return wholeDays / 7;
  if (inc === 'DAILY') return wholeDays;
  if (inc === 'HOURLY') {
    const remHours = Math.floor((diffMs % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
    return wholeDays * 24 + remHours;
  }
  return null;
}

function toDbFrequency(increment: string, peak: 'ON' | 'OFF' | null): string | null {
  const inc = increment.toUpperCase();
  if (inc === 'YEARLY') return 'years';
  if (inc === 'MONTHLY') return 'monthly';
  if (inc === 'WEEKLY') return 'weekly';
  if (inc === 'DAILY') return peak === 'OFF' ? 'daily_off_peak' : 'daily_on_peak';
  if (inc === 'HOURLY') return peak === 'OFF' ? 'hourly_off_peak' : 'hourly_on_peak';
  return null;
}

function getPeak(increment: string, start: Date | null): 'ON' | 'OFF' | null {
  const inc = increment.toUpperCase();
  if (!inc || inc === 'MONTHLY' || inc === 'YEARLY' || inc === 'WEEKLY') return null;
  if (!start) return null;
  const jsDay = start.getDay();
  const excelDay = jsDay === 0 ? 7 : jsDay;
  if (excelDay > 5) return 'OFF';
  if (inc === 'DAILY') return 'ON';
  if (inc === 'HOURLY') return start.getHours() > 7 ? 'ON' : 'OFF';
  return 'OFF';
}

const r2 = (v: number): number => parseFloat(v.toFixed(2));
const safe = (v: number | null | undefined): number =>
  v != null && !isNaN(Number(v)) ? Number(v) : 0;

type AncRateMap = Map<string, number>;

function buildAncRateMap(rates: AncillaryRate[]): AncRateMap {
  const map = new Map<string, number>();
  for (const r of rates) {
    const key = `${r.ancillary_service_type.toLowerCase().trim()}|${r.frequency.toLowerCase().trim()}`;
    map.set(key, r.rate);
  }
  return map;
}

function lookupAncRate(map: AncRateMap, serviceType: string, frequency: string | null): number {
  if (!frequency) return 0;
  const key = `${serviceType.toLowerCase().trim()}|${frequency.toLowerCase().trim()}`;
  return map.get(key) ?? 0;
}

function computeAncRates(ancRateMap: AncRateMap, increment: string, peak: string | null, dv: number): [number, number] {
  if (increment === 'HOURLY') {
    const onPkH = peak === 'ON' ? dv : 0;
    const offPkH = peak === 'OFF' ? dv : 0;
    const rSc1On = lookupAncRate(ancRateMap, ANC1_SERVICE, 'hourly_on_peak');
    const rSc1Off = lookupAncRate(ancRateMap, ANC1_SERVICE, 'hourly_off_peak');
    const rRv1On = lookupAncRate(ancRateMap, ANC2_SERVICE, 'hourly_on_peak');
    const rRv1Off = lookupAncRate(ancRateMap, ANC2_SERVICE, 'hourly_off_peak');
    const anc1Rate = dv > 0 ? (onPkH * rSc1On + offPkH * rSc1Off) / dv : 0;
    const anc2Rate = dv > 0 ? (onPkH * rRv1On + offPkH * rRv1Off) / dv : 0;
    return [anc1Rate, anc2Rate];
  }
  const freq = toDbFrequency(increment, peak as 'ON' | 'OFF' | null);
  return [
    lookupAncRate(ancRateMap, ANC1_SERVICE, freq),
    lookupAncRate(ancRateMap, ANC2_SERVICE, freq),
  ];
}

function computePreview(row: OattRevisionRecord, ancRateMap: AncRateMap): PreviewFields {
  const mwGranted = row.mw_granted == null ? null : Number(row.mw_granted);
  const billingRate = row.billing_rate == null ? null : Number(row.billing_rate);
  const aref = row.aref?.trim() ?? '';
  const increment = (row.increment ?? '').toUpperCase();
  const tsClass = (row.ts_class ?? '').toUpperCase();
  const tsType = (row.ts_type ?? '').toUpperCase();

  const start = parseDateLocal(row.start_time);
  const stop = parseDateLocal(row.stop_time);
  const dv = computeDurationValue(increment, aref, start, stop);
  const peak = getPeak(increment, start);

  if (mwGranted == null || billingRate == null || dv == null) {
    return { hours: row.hours, net_trans: row.net_trans, asr_sc_net: row.asr_sc_net, asr_rv_net: row.asr_rv_net, total_charges: row.total_charges };
  }

  const hours = row.hours;
  const net_trans = r2(mwGranted * billingRate * dv);
  const ratesAvailable = ancRateMap.size > 0;

  let asr_sc_net: number | null = row.asr_sc_net ?? null;
  let asr_rv_net: number | null = row.asr_rv_net ?? null;

  if (ratesAvailable && aref && tsType !== 'NETWORK') {
    const [anc1Rate, anc2Rate] = computeAncRates(ancRateMap, increment, peak, dv);
    if (anc1Rate !== 0 || anc2Rate !== 0) {
      asr_sc_net = r2(mwGranted * dv * anc1Rate);
      asr_rv_net = r2(mwGranted * dv * anc2Rate);
    }
  }

  const firm_charge = tsClass === 'FIRM' ? safe(net_trans) : 0;
  const nonfirm_charge = tsClass === 'NON-FIRM' ? safe(net_trans) : 0;
  const total_charges = r2(firm_charge + nonfirm_charge + safe(asr_sc_net) + safe(asr_rv_net));

  return { hours, net_trans, asr_sc_net, asr_rv_net, total_charges };
}

// ── Sub-components ────────────────────────────────────────────────────────────

const EditableText = ({ value, onChange }: { value: string | null | undefined; onChange: (v: string) => void }) => (
  <Input size="small" value={value ?? ''} onChange={(e) => onChange(e.target.value)} style={{ minWidth: 80 }} />
);

const EditableNumber = ({
  value, onChange, precision = 4,
}: { value: number | null | undefined; onChange: (v: number | null) => void; precision?: number }) => (
  <InputNumber size="small" value={value ?? undefined} onChange={(v) => onChange(v as number | null)} precision={precision} style={{ width: 110 }} />
);

const PreviewCell = ({ preview, stored, precision = 2 }: { preview: number | null; stored: number | null; precision?: number }) => {
  const displayValue = preview ?? stored;
  if (displayValue == null) return <Text>—</Text>;
  const storedNum = stored != null ? Number(stored) : null;
  const previewNum = preview != null ? Number(preview) : null;
  const changed = storedNum != null && previewNum != null && Math.abs(previewNum - storedNum) > 0.005;
  return (
    <Tooltip title={changed ? `Was: ${storedNum?.toFixed(precision)}` : undefined}>
      <Text style={changed ? { fontWeight: 600 } : undefined}>{displayValue.toFixed(precision)}</Text>
    </Tooltip>
  );
};

// ── Main component ────────────────────────────────────────────────────────────

const OattRevisionPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  dayjs.extend(utc);

  const { isOattBillPreparer } = useSession();
  const canEdit = isOattBillPreparer;
  const { customerId, setCustomerId, CustomerSelect } = useOattCustomer();
  const month = Number(searchParams.get('month')) || dayjs().subtract(1, 'month').month() + 1;
  const year = Number(searchParams.get('year')) || dayjs().subtract(1, 'month').year();
  const [revisionNo, setRevisionNo] = useState<number>(Number(searchParams.get('revisionNo')) || 1);
  const [revisionPageSize, setRevisionPageSize] = useState(DEFAULT_PAGE_SIZE);
  const monthLabel = dayjs(`${year}-${String(month).padStart(2, '0')}-01`).format('MMMM YYYY');

  const [editMap, setEditMap] = useState<EditMap>({});
  const [alertInfo, setAlertInfo] = useState<{ type: 'success' | 'error' | 'warning'; msg: string } | null>(null);
  const [showCommentError, setShowCommentError] = useState(false);

  const { data, loading, error, refetch } = useQuery<{ oattRevisionData: OattRevisionRecord[] }>(
    GET_OATT_REVISION_DATA,
    {
      variables: { month, year, revisionNo, customerId },
      fetchPolicy: 'cache-and-network',
      onCompleted: () => setEditMap({}),
    },
  );

  const { data: ancData } = useQuery<{ getOattAncillaryRates: AncillaryRate[] }>(GET_OATT_ANCILLARY_RATES);

  const [saveOattRevision, { loading: saving }] = useMutation(SAVE_OATT_REVISION);
  const [computeOattRevision, { loading: computing }] = useMutation(COMPUTE_OATT_REVISION);

  const { data: revData } = useQuery<{ oattRevisionNumbers: number[] }>(
    GET_OATT_REVISION_NUMBERS,
    { variables: { month, year, customerId } },
  );

  const latestRevNo = revData?.oattRevisionNumbers?.slice(-1)[0] ?? null;
  const rawRows: OattRevisionRecord[] = data?.oattRevisionData ?? [];
  const rows: OattRevisionRecord[] = rawRows.map((r) => ({ ...r, ...(editMap[r.oatt_data_id] ?? {}) }));

  /**
   * True when the server reports that all validation (oatt_computed) records for
   * this revision are approved. Once locked, the entire revision is read-only.
   */
  const isRevisionApproved = rawRows.length > 0 && rawRows[0].revision_approved === true;

  /** Effective edit permission: preparer role AND revision not yet approved. */
  const canEditRevision = canEdit && !isRevisionApproved;

  const ancRateMap = useMemo(() => buildAncRateMap(ancData?.getOattAncillaryRates ?? []), [ancData]);
  const previewMap = useMemo(() => {
    const map = new Map<number, PreviewFields>();
    for (const row of rows) map.set(row.oatt_data_id, computePreview(row, ancRateMap));
    return map;
  }, [rows, ancRateMap]);
  useEffect(() => {
    if (revData?.oattRevisionNumbers?.length && !searchParams.get('revisionNo')) {
      const latest = revData.oattRevisionNumbers.slice(-1)[0];
      setRevisionNo(latest);
    }
  }, [revData]);

  useEffect(() => {
    if (!ancData?.getOattAncillaryRates?.length) return;
    console.group('[OATT] Ancillary rate map diagnostics');
    console.log('Raw rates from API:', ancData.getOattAncillaryRates);
    console.log('Map keys:', [...ancRateMap.keys()]);
    console.groupEnd();

  }, [ancRateMap]);

  useEffect(() => {
    const urlCustomerId = searchParams.get('customerId');

    if (urlCustomerId) {
      setCustomerId(Number(urlCustomerId));
    }
  }, [searchParams]);

  const updateField = useCallback(
    <K extends keyof OattRevisionRecord>(id: number, field: K, value: OattRevisionRecord[K]) => {
      setEditMap((prev) => ({ ...prev, [id]: { ...(prev[id] ?? {}), [field]: value } }));
    },
    [],
  );

  const handleSave = async () => {
    setAlertInfo(null);
    const editedIds = Object.keys(editMap).map(Number);
    if (editedIds.length === 0) { setAlertInfo({ type: 'warning', msg: 'No changes to save.' }); return; }

    const missingComment = editedIds.some((id) => {
      const inMemory = editMap[id]?.changes_from_original;
      const fromDb = rawRows.find((r) => r.oatt_data_id === id)?.changes_from_original;
      const effective = inMemory !== undefined ? inMemory : fromDb;
      return !effective?.trim();
    });

    if (missingComment) {
      setShowCommentError(true);
      setAlertInfo({ type: 'warning', msg: 'Changes from Original comment is mandatory.' });
      return;
    }

    const records = editedIds.map((id) => ({ oatt_data_id: id, ...editMap[id] }));
    try {
      const res = await saveOattRevision({ variables: { records, customerId, } });
      const result = res.data?.saveOattRevision;
      if (result?.success) {
        setAlertInfo({ type: 'success', msg: result.message ?? 'Revision saved.' });
        setShowCommentError(false);
        refetch();
      } else {
        setAlertInfo({ type: 'warning', msg: result?.message ?? 'Save returned no result.' });
      }
    } catch (err: any) {
      setAlertInfo({ type: 'error', msg: `Save failed: ${err?.message ?? 'Unexpected error.'}` });
    }
  };

  const handleComputeRevision = async () => {
    setAlertInfo(null);
    try {
      const res = await computeOattRevision({ variables: { month, year, revisionNo, customerId } });
      const result = res.data?.computeOattRevision;
      if (result?.success) {
        setAlertInfo({
          type: 'success',
          msg: result.message ?? `Revision ${revisionNo} computation complete — ${result.count} record(s) computed.`,
        });
        setTimeout(() => navigate('/oati/computation'), 2000);
      } else {
        setAlertInfo({ type: 'warning', msg: result?.message ?? 'Computation returned no result.' });
      }
    } catch (err: any) {
      setAlertInfo({ type: 'error', msg: `Compute failed: ${err?.message ?? 'Unexpected error.'}` });
    }
  };

  // ── Columns (identical to original — omitted for brevity, paste back in) ──
  const columns: ColumnsType<OattRevisionRecord> = [
    { title: 'Service Seller', dataIndex: 'seller', key: 'seller', width: 120, sorter: (a, b) => a.seller.localeCompare(b.seller), render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'Reservation Ref #', dataIndex: 'aref', key: 'aref', width: 150, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'Customer Code', dataIndex: 'customer_code', key: 'customer_code', width: 130, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'Billing Period', dataIndex: 'billing_period', key: 'billing_period', width: 110, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'Start Time', dataIndex: 'start_time', key: 'start_time', width: 140, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
    { title: 'Stop Time', dataIndex: 'stop_time', key: 'stop_time', width: 140, render: (v) => (v ? dayjs(v).format('YYYY-MM-DD HH:mm') : '—') },
    { title: 'Hours', dataIndex: 'hours', key: 'hours', align: 'right', width: 80, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'Billing Type', dataIndex: 'billing_type', key: 'billing_type', width: 110, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'TS Class', dataIndex: 'ts_class', key: 'ts_class', width: 100, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'TS Type', dataIndex: 'ts_type', key: 'ts_type', width: 90, render: (v) => <Text>{v ?? '—'}</Text> },
    { title: 'TS Window', dataIndex: 'ts_window', key: 'ts_window', width: 100, render: (v) => <Text>{v ?? '—'}</Text> },
    {
      title: 'Billing Rate', dataIndex: 'billing_rate', key: 'billing_rate', align: 'right', width: 120,
      render: (v, rec) => canEditRevision
        ? <EditableNumber value={editMap[rec.oatt_data_id]?.billing_rate ?? v} onChange={(val) => updateField(rec.oatt_data_id, 'billing_rate', val)} />
        : <Text>{(editMap[rec.oatt_data_id]?.billing_rate ?? v) != null ? Number(editMap[rec.oatt_data_id]?.billing_rate ?? v).toFixed(4) : '—'}</Text>,
    },
    {
      title: 'MW Granted', dataIndex: 'mw_granted', key: 'mw_granted', align: 'right', width: 120,
      render: (v, rec) => canEditRevision
        ? <EditableNumber value={editMap[rec.oatt_data_id]?.mw_granted ?? v} onChange={(val) => updateField(rec.oatt_data_id, 'mw_granted', val)} precision={0} />
        : <Text>{(editMap[rec.oatt_data_id]?.mw_granted ?? v) != null ? String(editMap[rec.oatt_data_id]?.mw_granted ?? v) : '—'}</Text>,
    },
    {
      title: 'Increment', dataIndex: 'increment', key: 'increment', width: 90,
      render: (v, rec) => canEditRevision
        ? <EditableText value={editMap[rec.oatt_data_id]?.increment ?? v} onChange={(val) => updateField(rec.oatt_data_id, 'increment', val)} />
        : <Text>{editMap[rec.oatt_data_id]?.increment ?? v ?? '—'}</Text>,
    },
    { title: <Tooltip title="Live preview — value that will be stored on Save">Net Trans <span>*</span></Tooltip>, dataIndex: 'net_trans', key: 'net_trans', align: 'right', width: 120, render: (_v, rec) => <PreviewCell preview={previewMap.get(rec.oatt_data_id)?.net_trans ?? null} stored={rec.net_trans} precision={4} /> },
    { title: <Tooltip title="Live preview — derived from ancillary config rates">ASR SC Net <span>*</span></Tooltip>, dataIndex: 'asr_sc_net', key: 'asr_sc_net', align: 'right', width: 120, render: (_v, rec) => <PreviewCell preview={previewMap.get(rec.oatt_data_id)?.asr_sc_net ?? null} stored={rec.asr_sc_net} precision={2} /> },
    { title: <Tooltip title="Live preview — derived from ancillary config rates">ASR RV Net <span>*</span></Tooltip>, dataIndex: 'asr_rv_net', key: 'asr_rv_net', align: 'right', width: 120, render: (_v, rec) => <PreviewCell preview={previewMap.get(rec.oatt_data_id)?.asr_rv_net ?? null} stored={rec.asr_rv_net} precision={2} /> },
    { title: <Tooltip title="Live preview — value that will be stored on Save">Total Charges <span>*</span></Tooltip>, dataIndex: 'total_charges', key: 'total_charges', align: 'right', width: 130, render: (_v, rec) => <PreviewCell preview={previewMap.get(rec.oatt_data_id)?.total_charges ?? null} stored={rec.total_charges} precision={2} /> },
    {
      title: 'Changes from Original', dataIndex: 'changes_from_original', key: 'changes_from_original', width: 260,
      render: (v, rec) => {
        const val = editMap[rec.oatt_data_id]?.changes_from_original ?? v ?? '';
        if (!canEditRevision) return <Text>{val || '—'}</Text>;
        const isError = showCommentError && editMap[rec.oatt_data_id] && !val.trim();
        return (
          <div style={{ minWidth: 220 }}>
            {isError && <div style={{ color: 'red', fontSize: 12, marginBottom: 2 }}>Comment is required</div>}
            <Input value={val} onChange={(e) => updateField(rec.oatt_data_id, 'changes_from_original', e.target.value.replace(/[^\x20-\x7E -￿\uD800-\uDFFF]/g, ''))} placeholder="Enter comment" maxLength={200} status={isError ? 'error' : ''} style={{ minWidth: 220 }} />
          </div>
        );
      },
    },
  ];

  const customerLabel = rows.length > 0 ? rows[0].customer_code : '';

  return (
    <PageLayout
      title={`${customerLabel ? `${customerLabel} — ` : ''}${monthLabel} OATT Revision Data (Revision ${revisionNo})`}
      headerAccessoryView={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div className={styles.customerSelectWrapper}>
            <CustomerSelect />
          </div>
          {latestRevNo !== null && latestRevNo !== revisionNo && (
            <NSPButton
              size="small"
              onClick={() => {
                setRevisionNo(latestRevNo);
                navigate(
                  `/oati/revision?month=${month}&year=${year}&revisionNo=${latestRevNo}&customerId=${customerId}`,
                  { replace: true }
                );
              }}
            >
              View Latest (Rev {latestRevNo})
            </NSPButton>
          )}
          <Text type="secondary" style={{ fontSize: 12 }}>
            <span style={{ color: '#d46b08' }}>*</span> columns update live as you type
          </Text>
        </div>
      }
    >
      {isRevisionApproved && (
        <Alert
          type="info"
          message={`Revision ${revisionNo} is approved and locked. This data is read-only.`}
          showIcon
          className={styles.alertBanner}
          style={{ marginBottom: 12 }}
        />
      )}

      {alertInfo && (
        <Alert
          type={alertInfo.type}
          message={alertInfo.msg}
          showIcon
          closable
          onClose={() => setAlertInfo(null)}
          className={`${styles.alertBanner} ${alertInfo.type === 'success' ? styles.alertSuccess : alertInfo.type === 'warning' ? styles.alertWarning : styles.alertError}`}
          style={{ marginBottom: 12 }}
        />
      )}

      <div className={styles.content}>
        {error ? (
          <Empty description={`Error loading revision data: ${error.message}`} />
        ) : (
          <Table<OattRevisionRecord>
            dataSource={rows.map((r) => ({ ...r, key: r.oatt_data_id }))}
            columns={columns}
            loading={loading ? { indicator: <Spin size="large" /> } : false}
            rowKey="oatt_data_id"
            rowClassName={(rec) => (editMap[rec.oatt_data_id] ? styles.dirtyRow : '')}
            pagination={{ pageSize: revisionPageSize, showTotal: (total) => `Total ${total} revision record(s)`, showSizeChanger: true, pageSizeOptions: PAGE_SIZE_OPTIONS, onShowSizeChange: (_, size) => setRevisionPageSize(size) }}
            scroll={{ x: 'max-content', y: 'calc(100vh - 320px)' }}
            locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={`No revision ${revisionNo} data found for ${monthLabel}`} /> }}
          />
        )}
      </div>

      {canEdit && !isRevisionApproved && (
        <div className={styles.computeButtonContainer}>
          <NSPButton onClick={handleSave} loading={saving} disabled={rows.length === 0} style={{ marginRight: 8 }}>
            Save Revision
          </NSPButton>
          <NSPButton onClick={handleComputeRevision} loading={computing}>
            QC Compute OATT
          </NSPButton>
        </div>
      )}
    </PageLayout>
  );
};

export default OattRevisionPage;